'use client'
// =============================================================================
// HomeDashboard — l'accueil des usagers connectés (maquette « tableau de bord holographique »).
//
// Chaque chiffre, chaque ligne et chaque barre vient d'une requête réelle (agents, projets de l'organisation, tâches, missions).
// Une barre est une fraction calculée sur ces données ; quand la donnée n'existe pas, la barre dit « non renseigné » au lieu
// de dessiner un pourcentage. Source muette → la carte le dit. Aucun exemple, aucun compteur inventé.
// Cadres, icônes et barres : composants partagés de src/components/hud.
// =============================================================================
import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useOrgBrand } from '@/hooks/useOrgBrand'
import { HudFrame } from '@/components/hud/HudFrame'
import { HudIcon } from '@/components/hud/HudIcon'
import { HudProgress, HudRing } from '@/components/hud/HudProgress'

interface Agent { id: string; hostname?: string; name?: string; status: string; platform?: string }
interface Project { id: string; number: string | null; name: string; status: string }
interface Task { id: string; title: string; status: string; dueDate?: string | null; priority?: string }
interface Mission { id: string; objective: string; status: string }

type Src<T> = { state: 'off' } | { state: 'loading' } | { state: 'error' } | { state: 'ok'; items: T[] }

const API = process.env.NEXT_PUBLIC_API_URL ?? ''

function useList<T>(url: string | null, pick: (d: any) => T[] | null): Src<T> {
  const [s, setS] = useState<Src<T>>({ state: 'off' })
  useEffect(() => {
    if (!url) { setS({ state: 'off' }); return }
    let off = false
    setS({ state: 'loading' })
    fetch(url, { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(d => { const items = pick(d); if (!off) setS(items ? { state: 'ok', items } : { state: 'error' }) })
      .catch(() => { if (!off) setS({ state: 'error' }) })
    return () => { off = true }
  }, [url]) // eslint-disable-line react-hooks/exhaustive-deps
  return s
}

const pillClass = (st: string) =>
  /ACTIVE|ONLINE|DONE|RUNNING|COMPLETED|EN_COURS|IN_PROGRESS|EXECUTION/i.test(st) ? 'ok'
    : /WAIT|PENDING|TODO|REVIEW|PAUSED|IDLE|NEW|ANALYSIS|PLANNING|VALIDATION/i.test(st) ? 'warn'
    : /FAIL|ERROR|OFFLINE|BLOCK|CANCEL/i.test(st) ? 'bad' : 'idle'
const Pill = ({ s }: { s: string }) => <span className={`hx-pill ${pillClass(s)}`}>{s}</span>
const jour = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('fr-CA', { day: '2-digit', month: 'short' }) : '—')
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n > 1 ? many : one}`

/** Étapes du cycle d'une mission (schema.prisma, MissionStatus) : la position est une donnée réelle, pas un pourcentage de travail. */
const STAGES = ['NEW', 'ANALYSIS', 'PLANNING', 'EXECUTION', 'VALIDATION', 'COMPLETED'] as const
const STAGE_LABEL: Record<string, string> = { NEW: 'Nouvelle', ANALYSIS: 'Analyse', PLANNING: 'Planification', EXECUTION: 'Exécution', VALIDATION: 'Validation', COMPLETED: 'Terminée', WAITING_USER: 'Attend votre réponse', FAILED: 'Échec', CANCELLED: 'Annulée' }
function missionProgress(status: string): { value: number | null; detail: string; tone?: 'ok' | 'warn' | 'bad' } {
  const i = STAGES.indexOf(status as typeof STAGES[number])
  if (i >= 0) return { value: (i + 1) / STAGES.length, detail: `${STAGE_LABEL[status]} · étape ${i + 1}/${STAGES.length}`, tone: status === 'COMPLETED' ? 'ok' : undefined }
  return { value: null, detail: STAGE_LABEL[status] ?? status, tone: status === 'FAILED' ? 'bad' : 'warn' }
}

function Body<T>({ src, empty, children }: { src: Src<T>; empty: string; children: (items: T[]) => React.ReactNode }) {
  if (src.state === 'off') return <div className="hx-empty">Disponible une fois connecté à votre organisation.</div>
  if (src.state === 'loading') return <div className="hx-empty">Chargement…</div>
  if (src.state === 'error') return <div className="hx-empty">Source indisponible pour le moment.</div>
  if (!src.items.length) return <div className="hx-empty">{empty}</div>
  return <>{children(src.items)}</>
}

export function HomeDashboard({ firstName, userId }: { firstName: string | null; userId?: string }) {
  const org = useOrgBrand()
  const agents = useList<Agent>(`${API}/api/agents`, d => (Array.isArray(d?.agents) ? d.agents : null))
  const projects = useList<Project>(org ? `/api/crm/orgs/${org.id}/projects` : null, d => (Array.isArray(d?.projects) ? d.projects : null))
  const tasks = useList<Task>(userId ? `/api/tasks?assigneeId=${encodeURIComponent(userId)}&limit=50` : null, d => (Array.isArray(d?.items) ? d.items : null))
  const missions = useList<Mission>('/api/missions?limit=50', d => (Array.isArray(d?.missions) ? d.missions : Array.isArray(d) ? d : null))

  const okItems = <T,>(s: Src<T>): T[] | null => (s.state === 'ok' ? s.items : null)
  const A = okItems(agents), P = okItems(projects), T = okItems(tasks), M = okItems(missions)

  const online = A ? A.filter(a => a.status === 'ONLINE').length : null
  const toDo = T ? T.filter(t => ['TODO', 'IN_PROGRESS', 'REVIEW'].includes(t.status)) : null
  const done = T ? T.filter(t => t.status === 'DONE').length : null
  const waiting = M ? M.filter(m => m.status === 'WAITING_USER').length : null
  const isClosed = (s: string) => /CLOS|DONE|ARCHIV|CANCEL|TERMIN/i.test(s)
  const activeProjects = P ? P.filter(p => !isClosed(p.status)).length : null
  const byStatus = M ? Object.entries(M.reduce<Record<string, number>>((acc, m) => { acc[m.status] = (acc[m.status] ?? 0) + 1; return acc }, {})) : []
  const missionsDone = M ? M.filter(m => m.status === 'COMPLETED').length : null

  return (
    <div className="hx-home">
      <HudFrame className="hx-greet" title="Salutations à l’usager" icon="salutations">
        <p className="hx-hello">Salutations{firstName ? `, ${firstName}` : ''}.</p>
        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{new Date().toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}{org ? ` · ${org.name}` : ''}</div>
        {/* Le résumé ne contient que des lignes dont la source a répondu. */}
        <ul>
          {toDo !== null && <li>{toDo.length ? `${plural(toDo.length, 'tâche')} à faire` : 'Aucune tâche à faire'}</li>}
          {waiting !== null && <li>{waiting ? `${plural(waiting, 'mission attend', 'missions attendent')} votre réponse` : 'Aucune mission n’attend votre réponse'}</li>}
          {activeProjects !== null && <li>{plural(activeProjects, 'projet actif', 'projets actifs')}</li>}
          {online !== null && <li>{plural(online, 'agent')} en ligne</li>}
          {toDo === null && waiting === null && activeProjects === null && online === null && <li>Connectez-vous pour voir le résumé de votre journée.</li>}
        </ul>
        {org && (org.websiteUrl || org.contactEmail || org.contactPhone) && (
          <div className="hx-brand-note">
            {org.websiteUrl && <a href={org.websiteUrl} target="_blank" rel="noopener noreferrer">{org.websiteUrl.replace(/^https:\/\//, '').replace(/\/$/, '')}</a>}
            {org.contactEmail && <a href={`mailto:${org.contactEmail}`}>{org.contactEmail}</a>}
            {org.contactPhone && <a href={`tel:${org.contactPhone.replace(/[^\d+]/g, '')}`}>{org.contactPhone}</a>}
          </div>
        )}
        {org && !org.logoSrc && org.canEdit && (
          <div className="hx-brand-note">Aucun logo configuré pour {org.name} : <Link href="/parametres/organisation">ajouter l’identité de l’organisation →</Link></div>
        )}
      </HudFrame>

      <HudFrame className="hx-network" title="Amiral Network" icon="reseau">
        <div className="hx-split">
          <HudRing value={A && A.length ? (online ?? 0) / A.length : null} label="Agents en ligne" unavailable={agents.state === 'ok' ? '—' : '…'} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="hx-stat" style={{ borderTop: 0 }}><span>Agents en ligne</span><b>{online ?? '—'}</b></div>
            <div className="hx-stat"><span>Agents connus</span><b>{A ? A.length : '—'}</b></div>
          </div>
        </div>
        <div className="hx-stat"><span>Organisation</span><b style={{ fontFamily: 'inherit' }}>{org?.name ?? '—'}</b></div>
      </HudFrame>

      <HudFrame className="hx-projects" title="État des projets" icon="projets" action={<Link href="/projets">Tout voir →</Link>}>
        <Body src={projects} empty={org ? 'Aucun projet.' : 'Aucune organisation branchée.'}>
          {items => (<>
            <HudProgress value={items.length ? (activeProjects ?? 0) / items.length : null} label="Projets actifs" detail={`${activeProjects ?? 0} / ${items.length}`} />
            <table className="hx-table" style={{ marginTop: 10 }}><thead><tr><th>Projet</th><th>État</th></tr></thead>
              <tbody>{items.slice(0, 6).map(p => <tr key={p.id}><td>{p.number ? `${p.number} · ` : ''}{p.name}</td><td><Pill s={p.status} /></td></tr>)}</tbody></table>
          </>)}
        </Body>
      </HudFrame>

      <HudFrame className="hx-agents" title="Agents IA" icon="agents" action={<Link href="/parametres?tab=agents">Gérer →</Link>}>
        <Body src={agents} empty="Aucun agent appairé.">
          {items => items.slice(0, 6).map(a => <div className="hx-stat" key={a.id}><span>{a.name ?? a.hostname ?? a.id}</span><Pill s={a.status} /></div>)}
        </Body>
      </HudFrame>

      <HudFrame className="hx-radar" title="Radar" icon="radar">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img src="/radar_amiral.png" alt="Illustration du radar Amiral" />
        <div className="hx-empty">Illustration — ne représente pas de données en direct.</div>
      </HudFrame>

      <HudFrame className="hx-proc" title="Processus des agents IA" icon="processus">
        <Body src={missions} empty="Aucune mission en cours.">
          {items => byStatus.map(([st, n]) => (
            <div className="hx-rowp" key={st}><HudProgress value={n / items.length} label={STAGE_LABEL[st] ?? st} detail={`${n} / ${items.length}`} tone={st === 'FAILED' ? 'bad' : st === 'COMPLETED' ? 'ok' : st === 'WAITING_USER' ? 'warn' : undefined} /></div>
          ))}
        </Body>
      </HudFrame>

      <HudFrame className="hx-tasks" title="Tâches" icon="taches" action={<Link href="/bureau/tasks">Tout voir →</Link>}>
        <Body src={tasks} empty="Aucune tâche assignée.">
          {items => (<>
            <HudProgress value={items.length ? (done ?? 0) / items.length : null} label="Terminées" detail={`${done ?? 0} / ${items.length}`} tone="ok" />
            <table className="hx-table" style={{ marginTop: 10 }}><thead><tr><th>Tâche</th><th>Échéance</th><th>État</th></tr></thead>
              <tbody>{items.slice(0, 6).map(t => <tr key={t.id}><td>{t.title}</td><td>{jour(t.dueDate)}</td><td><Pill s={t.status} /></td></tr>)}</tbody></table>
          </>)}
        </Body>
      </HudFrame>

      <HudFrame className="hx-missions" title="Missions en cours" icon="missions" action={<Link href="/projets">Tout voir →</Link>}>
        <Body src={missions} empty="Aucune mission.">
          {items => (<>
            <HudProgress value={items.length ? (missionsDone ?? 0) / items.length : null} label="Terminées" detail={`${missionsDone ?? 0} / ${items.length}`} tone="ok" />
            <div style={{ marginTop: 10 }}>
              {items.slice(0, 5).map(m => { const p = missionProgress(m.status); return (
                <div className="hx-rowp" key={m.id}>
                  <Link href={`/missions/${m.id}`} className="hx-rowp-t" style={{ color: 'inherit', textDecoration: 'none' }}>{m.objective}</Link>
                  <HudProgress value={p.value} detail={p.detail} tone={p.tone} unavailable={p.detail} />
                </div>) })}
            </div>
          </>)}
        </Body>
      </HudFrame>
    </div>
  )
}
